import { useReservationModalTranslation } from "../reservations/reservationModalI18n";
/**
 * COMPONENT: ReservationExtrasModal.jsx
 * Gestiona la asignación de extras a reservas (mismo estilo que RoomBlocks).
 * La UI comienza con un selector desplegable de Extras disponibles.
 */

import { useEffect, useMemo, useState } from "react";
import axios from "../api/axiosInstance";
import { useCurrencyDisplay } from "../currency/CurrencyDisplayContext";
import { formatCurrencyAmount } from "../currency/currencyPrecision";
import {
  buildReservationExtrasAssignmentPayload,
  formatReservationExtraCatalogAmount,
  getReservationExtraStayDates,
  getSelectableReservationExtras,
} from "./reservationExtrasEligibility";
import "./ReservationExtrasModal.css";

const ReservationExtrasModal = ({ show, onClose, reservationId, businessUnitId, onSaved }) => {
  const { t: tr, locale, errorText } = useReservationModalTranslation();
  const { baseCurrency, currencyCatalog = [] } = useCurrencyDisplay();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [extras, setExtras] = useState([]);
  const [selectedExtras, setSelectedExtras] = useState([]);
  const [draftSummary, setDraftSummary] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");

  const selectedPreviewExtras = useMemo(
    () => buildReservationExtrasAssignmentPayload(selectedExtras).extras,
    [selectedExtras]
  );
  const selectionKey = JSON.stringify(selectedPreviewExtras);
  const stayDates = useMemo(() => getReservationExtraStayDates(
    draftSummary?.checkIn, draftSummary?.checkOut
  ), [draftSummary?.checkIn, draftSummary?.checkOut]);
  const invalidNightSelection = selectedExtras.some((extra) =>
    extra.charge_period === "per_night" && Array.isArray(extra.selected_stay_dates) &&
    (extra.selected_stay_dates.length === 0 || extra.selected_stay_dates.some((date) => !stayDates.includes(date)))
  );
  const previewIsCurrent = preview?.selectionKey === selectionKey;
  const previewPending = !invalidNightSelection && !previewError && (previewLoading || !previewIsCurrent);

    useEffect(() => {
      if (!show) return;
      const fetchExtras = async () => {
        try {
          setLoading(true);
          setError("");
          setPreview(null);
          setPreviewError("");
          setDraftSummary(null);
          setSelectedExtras([]);

          // 1. Catálogo de extras de la BU
          const res = await axios.get("/api/extras", {
            params: { business_unit_id: businessUnitId },
          });

          const all = Array.isArray(res.data) ? res.data : [];
          const filteredExtras = getSelectableReservationExtras(all);
          setExtras(filteredExtras);



          // 2. Extras ya aplicados en el DRAFT
          const draft = await axios.get(`/api/reservation-drafts/${reservationId}`);
          const draftData = draft.data || {};
          const applied = draftData.extras || [];
          const appliedExtras = getSelectableReservationExtras(
            applied.filter((row) => row.extra).map((row) => ({
              ...row.extra,
              selected_stay_dates: row.selected_stay_dates ?? null,
            }))
          );

          console.log("[ExtrasModal][UI] Applied extras from draft:", applied);

          const baseTotal = Number(draftData.total_price || 0);
          const currentExtras = Number(draftData.total_extras || 0);
          const currency = baseCurrency;

          setDraftSummary({
            base: baseTotal,
            currentExtras,
            currentFinal: baseTotal + currentExtras,
            currency,
            checkIn: draftData.check_in_date,
            checkOut: draftData.check_out_date,
          });

          // 3. Inicializar selección con los extras aplicados
          // Defensive: filter out rows without loaded extra to avoid render crash
          setSelectedExtras(appliedExtras);


        } catch (err) {
          console.error("[ExtrasModal][UI] Error fetching extras:", err);
          setError(err?.response?.data?.error || err?.response?.data?.message || "Error loading extras.");
        } finally {
          setLoading(false);
        }
      };
      fetchExtras();
    }, [baseCurrency, show, businessUnitId, reservationId]);

  useEffect(() => {
    if (!show || !reservationId || !draftSummary) return;
    if (invalidNightSelection) {
      setPreview(null);
      setPreviewError("");
      setPreviewLoading(false);
      return;
    }

    let cancelled = false;
    const fetchPreview = async () => {
      try {
        setPreviewLoading(true);
        setPreview(null);
        setPreviewError("");
        const res = await axios.post(
          `/api/reservation-drafts/extras/preview/${reservationId}`,
          {
            extras: selectedPreviewExtras,
          }
        );

        if (!cancelled) {
          setPreview({ ...res.data, selectionKey });
        }
      } catch (err) {
        console.error("[ExtrasModal][UI] Error previewing extras:", err);
        if (!cancelled) {
          setPreview(null);
          setPreviewError(
            err?.response?.data?.error ||
              err?.response?.data?.message ||
              "Error calculating extras impact."
          );
        }
      } finally {
        if (!cancelled) {
          setPreviewLoading(false);
        }
      }
    };

    fetchPreview();

    return () => {
      cancelled = true;
    };
  }, [show, reservationId, selectedPreviewExtras, selectionKey, draftSummary, invalidNightSelection]);


  const addExtra = (id) => {
    const ex = extras.find((e) => String(e.id) === String(id));
    if (!ex) return;
    if (!selectedExtras.find((s) => String(s.id) === String(ex.id))) {
      setSelectedExtras((prev) => [...prev, ex]);
    }
  };

  const removeExtra = (id) => {
    setSelectedExtras((prev) => prev.filter((e) => String(e.id) !== String(id)));
  };

  const updateExtraNights = (id, dates) => {
    setSelectedExtras((previous) => previous.map((extra) =>
      String(extra.id) === String(id) ? { ...extra, selected_stay_dates: dates } : extra
    ));
  };

    const handleSave = async () => {
      if (loading || saving || invalidNightSelection || previewLoading || previewError || !previewIsCurrent) return;
      try {
        setSaving(true);
        setError("");
        console.log("[ExtrasModal][UI] Saving extras (full replace)", {
          reservationId,
          businessUnitId,
          selected: selectedExtras.map((ex) => ex.id),
        });

        // ✅ Siempre enviamos la lista completa (puede ser vacía → elimina todos)
                const res = await axios.post(
          `/api/reservation-drafts/extras/assign/${reservationId}`,
          buildReservationExtrasAssignmentPayload(selectedExtras)
        );

        console.log("[ExtrasModal][UI] ✅ Extras saved:", selectedExtras, res.data.totals);

        // 🔹 Pasar totales y rows al padre
        let saveResult = null;
        if (onSaved) {
          saveResult = await onSaved({
            totals: res.data.totals,
            rows: res.data.rows,
          });
        }

        // ❌ No vaciamos selectedExtras para evitar duplicados al reabrir
        if (!saveResult?.skipClose) {
          onClose();
        }
      } catch (err) {
        console.error("[ExtrasModal][UI] ❌ Error saving extras:", err);
        setError(err?.response?.data?.error || err?.response?.data?.message || err?.message || "Error saving extras.");
      } finally {
        setSaving(false);
      }
    };


  function formatExtraDescription(ex) {
    if (!ex) return "";
    const amountStr = formatReservationExtraCatalogAmount(ex, baseCurrency, currencyCatalog);
    const unit = (ex.charge_unit || "").replace("per_", "") || "room";
    const period = (ex.charge_period || "").replace("per_", "") || "stay";
    return tr("{amount} per {unit} / {period}", { amount: amountStr, unit: tr(unit), period: tr(period) });
    }

  const formatMoney = (value, currency = baseCurrency) =>
    formatCurrencyAmount(value || 0, currency, { locale, catalog: currencyCatalog });

  const pricePreviewTotals = previewIsCurrent ? preview?.totals : null;
  const summaryCurrency =
    draftSummary?.currency ||
    selectedExtras.find((extra) => extra?.currency)?.currency ||
    baseCurrency;
  const currentFinal = Number(draftSummary?.currentFinal || 0);
  const previewFinal = Number(pricePreviewTotals?.final ?? currentFinal);
  const previewExtraTotal = Number(pricePreviewTotals?.extras ?? draftSummary?.currentExtras ?? 0);
  const totalDifference = previewFinal - currentFinal;


  if (!show) return null;

  const overlayStyle = {
    position: "fixed",
    inset: 0,
    zIndex: 1050,
    backgroundColor: "rgba(0, 0, 0, 0.75)",
    backdropFilter: "blur(8px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "32px 16px",
  };

  const panelStyle = {
    width: "min(700px, 100%)",
    maxHeight: "90vh",
    overflowY: "auto",
    background: "linear-gradient(145deg, rgba(8, 8, 18, 0.95), rgba(14, 14, 32, 0.98))",
    borderRadius: "28px",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    padding: "26px 28px",
    boxShadow: "0 30px 80px rgba(0, 0, 0, 0.65)",
  };

  const cardStyle = {
    borderRadius: "18px",
    border: "1px solid transparent",
    background:
      "linear-gradient(180deg, rgba(25, 25, 51, 0.94), rgba(10, 10, 22, 0.94)) padding-box, " +
      "linear-gradient(135deg, rgba(48, 216, 255, 0.55), rgba(168, 85, 247, 0.65), rgba(255, 77, 79, 0.45)) border-box",
    boxShadow: "0 24px 70px rgba(0, 0, 0, 0.58)",
    padding: "22px",
  };

  const sectionStyle = {
    borderRadius: "16px",
    border: "1px solid rgba(255, 255, 255, 0.09)",
    background: "linear-gradient(180deg, rgba(18, 18, 40, 0.92), rgba(12, 12, 22, 0.98))",
    padding: "16px",
    marginBottom: "16px",
    boxShadow: "0 22px 45px rgba(0, 0, 0, 0.35)",
  };

  const labelStyle = {
    color: "rgba(225, 232, 250, 0.78)",
    fontSize: "0.72rem",
    letterSpacing: "0.25px",
    marginBottom: "6px",
    fontWeight: 600,
    textTransform: "uppercase",
    display: "block",
  };

  const selectStyle = {
    background: "rgba(20, 20, 44, 0.9)",
    color: "rgba(236, 240, 255, 0.96)",
    border: "1px solid rgba(255, 255, 255, 0.16)",
    borderRadius: "10px",
    padding: "9px 12px",
    fontSize: "0.88rem",
    width: "100%",
    boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.04)",
    outline: "none",
    appearance: "auto",
  };

  const chipAreaStyle = {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    background: "rgba(15, 15, 35, 0.7)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    borderRadius: "12px",
    padding: "12px",
    minHeight: "54px",
  };

  const chipStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    border: "1px solid rgba(48, 216, 255, 0.45)",
    borderRadius: "999px",
    padding: "5px 12px",
    fontSize: "0.82rem",
    background: "rgba(48, 216, 255, 0.08)",
    color: "rgba(186, 230, 253, 0.92)",
    fontWeight: 500,
  };

  const priceRowStyle = {
    display: "flex",
    justifyContent: "space-between",
    gap: "14px",
    padding: "8px 0",
    borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
    color: "rgba(226, 232, 240, 0.74)",
    fontSize: "0.86rem",
  };

  const priceValueStyle = {
    color: "#f8fafc",
    fontWeight: 800,
    whiteSpace: "nowrap",
  };

  const btnCancelStyle = {
    flex: 1,
    borderRadius: "12px",
    border: "1px solid rgba(248, 113, 113, 0.72)",
    color: "#fecaca",
    background: "rgba(127, 29, 29, 0.2)",
    padding: "10px 20px",
    fontSize: "0.92rem",
    fontWeight: 700,
    letterSpacing: "0.03em",
    cursor: "pointer",
    transition: "transform 140ms ease, box-shadow 140ms ease",
  };

  const btnSaveStyle = {
    flex: 1,
    borderRadius: "12px",
    border: "1px solid rgba(56, 189, 248, 0.8)",
    color: "#eff6ff",
    background: "linear-gradient(135deg, rgba(14, 165, 233, 0.82), rgba(59, 130, 246, 0.92))",
    boxShadow: "0 14px 30px rgba(14, 165, 233, 0.28)",
    padding: "10px 20px",
    fontSize: "0.92rem",
    fontWeight: 700,
    letterSpacing: "0.03em",
    cursor: "pointer",
    transition: "transform 140ms ease, box-shadow 140ms ease",
  };

  const closeStyle = {
    width: "40px",
    height: "40px",
    flexShrink: 0,
    borderRadius: "12px",
    border: "1px solid rgba(148, 163, 184, 0.32)",
    background: "rgba(15, 23, 42, 0.7)",
    color: "#e2e8f0",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.15rem",
    cursor: "pointer",
    lineHeight: 1,
  };

  return (
    <div className="rgv-linkedOverlay rgv-reservationExtrasOverlay" style={overlayStyle} onClick={saving ? undefined : onClose}>
      <div className="rgv-reservationExtrasFrame" style={panelStyle} onClick={(e) => e.stopPropagation()}>
        <div className="rgv-linkedModal rgv-reservationExtrasModal" style={cardStyle}>

          {/* Header */}
          <div className="rgv-linkedHeader" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "20px" }}>
            <div>
              <p style={{ margin: 0, fontSize: "0.72rem", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(186, 230, 253, 0.82)" }}> {tr("Reservation")} </p>
              <h2 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 800, color: "#f8fafc" }}> {tr("Manage Extras")} </h2>
            </div>
            <button
              type="button"
              style={closeStyle}
              onClick={onClose}
              aria-label={tr("Close")}
              disabled={saving}
            >
              ×
            </button>
          </div>

          {loading ? (
            <p style={{ color: "rgba(226, 232, 240, 0.68)", fontSize: "0.9rem" }}>{tr("Loading extras…")}</p>
          ) : (
            <>
              {/* Selector de Extras */}
              <div className="rgv-linkedSection" style={sectionStyle}>
                <p style={{ margin: "0 0 10px", fontSize: "0.74rem", fontWeight: 700, letterSpacing: "0.85px", textTransform: "uppercase", color: "rgba(226, 235, 255, 0.7)", borderBottom: "1px solid rgba(255,255,255,0.12)", paddingBottom: "7px" }}> {tr("Add Extra")} </p>
                <label style={labelStyle}>{tr("Select Extra")}</label>
                <select
                  style={selectStyle}
                  disabled={saving}
                  onChange={(e) => {
                    if (e.target.value) {
                      addExtra(e.target.value);
                      e.target.value = "";
                    }
                  }}
                >
                  <option value="">{tr("Choose an extra to add…")}</option>
                  {extras.map((ex) => (
                    <option key={ex.id} value={ex.id}>
                      {ex.name} — {formatExtraDescription(ex)}
                    </option>
                  ))}
                </select>
                <small style={{ color: "rgba(226, 232, 240, 0.48)", fontSize: "0.75rem", marginTop: "5px", display: "block" }}> {tr("Pick one; it will be added to the list below. Repeat to add more.")} </small>
              </div>

              {/* Lista de Extras seleccionados */}
              <div className="rgv-linkedSection" style={sectionStyle}>
                <p style={{ margin: "0 0 10px", fontSize: "0.74rem", fontWeight: 700, letterSpacing: "0.85px", textTransform: "uppercase", color: "rgba(226, 235, 255, 0.7)", borderBottom: "1px solid rgba(255,255,255,0.12)", paddingBottom: "7px" }}> {tr("Selected Extras")} </p>
                <div className="rgv-linkedChipArea" style={chipAreaStyle}>
                  {selectedExtras.length === 0 && (
                    <span style={{ color: "rgba(226, 232, 240, 0.48)", fontSize: "0.85rem" }}> {tr("No extras selected yet.")} </span>
                  )}
                  {selectedExtras
                    .filter(Boolean)
                    .map((ex) => (
                      <div key={ex.id} className="reservation-extra-selection">
                      <span className="rgv-linkedChip" style={chipStyle}>
                        {ex.name} — {formatExtraDescription(ex)}
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => removeExtra(ex.id)}
                          style={{
                            lineHeight: 1,
                            border: "none",
                            background: "transparent",
                            color: "#fca5a5",
                            padding: 0,
                            cursor: "pointer",
                            fontSize: "1.1rem",
                            fontWeight: 700,
                          }}
                          aria-label={tr("Remove {name}", { name: ex.name })}
                          title={tr("Remove")}
                        >
                          ×
                        </button>
                      </span>
                      {ex.charge_period === "per_night" && (
                        <fieldset className="reservation-extra-nights" disabled={saving}>
                          <legend>{tr("Nights for {name}", { name: ex.name })}</legend>
                          <select
                            style={selectStyle}
                            aria-label={tr("Nights for {name}", { name: ex.name })}
                            value={Array.isArray(ex.selected_stay_dates) ? "specific" : "all"}
                            onChange={(event) => updateExtraNights(ex.id, event.target.value === "specific" ? [...stayDates] : null)}
                          >
                            <option value="all">{tr("All nights")}</option>
                            <option value="specific">{tr("Specific nights")}</option>
                          </select>
                          {Array.isArray(ex.selected_stay_dates) && (
                            <>
                              <p>{tr("Select the nights when this extra is needed. The checkout date is not a night of the stay.")}</p>
                              <div className="reservation-extra-night-options">
                                {stayDates.map((date) => (
                                  <label key={date}>
                                    <input
                                      type="checkbox"
                                      value={date}
                                      checked={ex.selected_stay_dates.includes(date)}
                                      onChange={(event) => updateExtraNights(ex.id, event.target.checked
                                        ? [...ex.selected_stay_dates, date].sort()
                                        : ex.selected_stay_dates.filter((selected) => selected !== date))}
                                    />
                                    {new Date(`${date}T00:00:00Z`).toLocaleDateString(locale, {
                                      weekday: "short", day: "numeric", month: "short", year: "numeric", timeZone: "UTC",
                                    })}
                                  </label>
                                ))}
                              </div>
                            </>
                          )}
                        </fieldset>
                      )}
                      </div>
                    ))}
                </div>
                {invalidNightSelection && (
                  <p role="alert" className="reservation-extra-night-error">{tr("Select at least one night for each nightly extra.")}</p>
                )}
              </div>

              <div className="rgv-linkedSection rgv-reservationExtrasPriceImpact" style={sectionStyle}>
                <p className="rgv-reservationExtrasPriceTitle" style={{ margin: "0 0 10px", fontSize: "0.74rem", fontWeight: 700, letterSpacing: "0.85px", textTransform: "uppercase", color: "rgba(226, 235, 255, 0.7)", borderBottom: "1px solid rgba(255,255,255,0.12)", paddingBottom: "7px" }}> {tr("Price Impact")} </p>
                <div>
                  <div className="rgv-reservationExtrasPriceRow" style={priceRowStyle}>
                    <span>{tr("Room price")}</span>
                    <span className="rgv-reservationExtrasPriceValue" style={priceValueStyle}>
                      {formatMoney(draftSummary?.base, summaryCurrency)}
                    </span>
                  </div>
                  <div className="rgv-reservationExtrasPriceRow" style={priceRowStyle}>
                    <span>{tr("Current extras")}</span>
                    <span className="rgv-reservationExtrasPriceValue" style={priceValueStyle}>
                      {formatMoney(draftSummary?.currentExtras, summaryCurrency)}
                    </span>
                  </div>
                  <div className="rgv-reservationExtrasPriceRow" style={priceRowStyle}>
                    <span>{tr("Selected extras")}</span>
                    <span className="rgv-reservationExtrasPriceValue" style={priceValueStyle}>
                      {previewPending
                        ? tr("Calculating...")
                        : previewIsCurrent ? formatMoney(previewExtraTotal, summaryCurrency) : "—"}
                    </span>
                  </div>
                  <div className="rgv-reservationExtrasPriceRow rgv-reservationExtrasPriceRow--total" style={{ ...priceRowStyle, borderBottom: "none", paddingTop: "10px" }}>
                    <span>{tr("New total price")}</span>
                    <span className="rgv-reservationExtrasPriceValue rgv-reservationExtrasPriceValue--total" style={{ ...priceValueStyle, color: "#bae6fd", fontSize: "0.98rem" }}>
                      {previewPending
                        ? tr("Calculating...")
                        : previewIsCurrent ? formatMoney(previewFinal, summaryCurrency) : "—"}
                    </span>
                  </div>
                  {!previewLoading && previewIsCurrent && (
                    <div className={`rgv-reservationExtrasDifference ${
                      totalDifference > 0
                        ? "rgv-reservationExtrasDifference--positive"
                        : totalDifference < 0
                          ? "rgv-reservationExtrasDifference--negative"
                          : "rgv-reservationExtrasDifference--neutral"
                    }`} style={{
                      marginTop: "8px",
                      borderRadius: "12px",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                      background:
                        totalDifference > 0
                          ? "rgba(22, 101, 52, 0.22)"
                          : totalDifference < 0
                            ? "rgba(127, 29, 29, 0.22)"
                            : "rgba(15, 23, 42, 0.42)",
                      color:
                        totalDifference > 0
                          ? "#bbf7d0"
                          : totalDifference < 0
                            ? "#fecaca"
                            : "rgba(226, 232, 240, 0.72)",
                      padding: "10px 12px",
                      fontSize: "0.86rem",
                      fontWeight: 700,
                    }}> {tr("Difference vs current total:")} {formatMoney(totalDifference, summaryCurrency)}
                    </div>
                  )}
                  {previewError ? (
                    <div style={{
                      marginTop: "8px",
                      borderRadius: "10px",
                      border: "1px solid rgba(248, 113, 113, 0.38)",
                      background: "rgba(127, 29, 29, 0.18)",
                      color: "#fecaca",
                      padding: "8px 10px",
                      fontSize: "0.82rem",
                    }}>
                      {errorText(previewError, "Error calculating extras impact.")}
                    </div>
                  ) : null}
                </div>
              </div>
            </>
          )}

          {error ? (
            <div style={{
              border: "1px solid rgba(248, 113, 113, 0.45)",
              borderRadius: "12px",
              background: "rgba(127, 29, 29, 0.24)",
              color: "#fecaca",
              padding: "10px 12px",
              fontSize: "0.86rem",
              marginTop: "10px",
            }}>
              {errorText(error, "Error saving extras.")}
            </div>
          ) : null}

          {/* Botones */}
          <div className="rgv-linkedActions" style={{ display: "flex", gap: "12px", marginTop: "8px" }}>
            <button
              type="button"
              style={btnCancelStyle}
              disabled={saving}
              onClick={() => {
                setSelectedExtras([]);
                onClose();
              }}
            > {tr("Cancel")} </button>
            <button
              type="button"
              style={btnSaveStyle}
              disabled={loading || saving || invalidNightSelection || previewLoading || Boolean(previewError) || !previewIsCurrent}
              onClick={handleSave}
            >
              {saving ? tr("Saving...") : tr("Save Extras")}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ReservationExtrasModal;
